import {withToken} from '@pexip/infinity-api';
import {Backoff} from '@pexip/utils';

import {
    backoffBaseOptions,
    DEFAULT_TOKEN_EXPIRES_SECS,
    TOKEN_VALIDITY_BUFFER_MS,
} from './constants';
import {logger} from './logger';
import type {
    DisconnectReason,
    RequestClient,
    RequestClientOptions,
} from './types';

export const createRequestClient = ({
    backoff = new Backoff(backoffBaseOptions),
    expires = DEFAULT_TOKEN_EXPIRES_SECS,
    fetcher = window.fetch,
    token,
    clientStatsUpdateInterval,
    tokenExpiredCb,
    tokenRefreshedCb,
    refreshTokenFn,
    releaseTokenFn,
    releaseTokenBeaconUrl,
}: RequestClientOptions): RequestClient => {
    let refreshPromise: Promise<void> | undefined;
    let lastRefreshTs = Date.now();
    let statsUpdateInterval = clientStatsUpdateInterval;

    const refreshInterval = setInterval(
        () => {
            void handleRefresh().catch(error => {
                logger.warn('Unrecoverable token refresh failure', error);
            });
        },
        (expires * 1000) / 3,
    );

    const handleRefresh = async () => {
        if (refreshPromise) {
            logger.debug(
                'Refresh token already in progress. Returning pending one.',
            );
            return refreshPromise;
        }
        refreshPromise = doRefresh();
        return refreshPromise;
    };

    const isTokenValid = () =>
        lastRefreshTs + expires * 1000 > Date.now() - TOKEN_VALIDITY_BUFFER_MS;

    const attemptRefresh = async () => {
        const res = await refreshTokenFn(withToken(fetcher, token));
        if (res?.status === 200) {
            lastRefreshTs = Date.now();
            token = res.data.result.token;
            statsUpdateInterval = res.data.result.client_stats_update_interval;
            return res;
        }
        return undefined;
    };

    const doRefresh = async () => {
        while (isTokenValid()) {
            logger.debug('trying to refresh token');
            const res = await attemptRefresh().catch(async error => {
                logger.warn(
                    'Failed to refresh token, retrying after backoff',
                    error,
                );
                await backoff.promise();
            });
            if (res) {
                tokenRefreshedCb?.(token);
                logger.debug('Token refreshed. Cleanup refreshPromise.');
                refreshPromise = undefined;
                backoff.reset();

                return;
            }
        }

        backoff.reset();
        tokenExpiredCb?.();

        throw new Error('Unable to update access token');
    };

    const fireAndForgetRefresh = async () => {
        if (!isTokenValid()) {
            logger.warn('Token has already expired. Cannot refresh.');
            return false;
        }
        const res = await attemptRefresh().catch(error => {
            logger.warn('Failed to refresh token. Error: ', error);
        });
        if (res) {
            logger.debug('Token refreshed');
            return true;
        }
        logger.warn('Failed to refresh token.');
        return false;
    };

    const cleanup = async (reason?: DisconnectReason) => {
        lastRefreshTs = 0; // explicitly invalidates the token as we re cleaning up
        statsUpdateInterval = undefined;
        if (refreshInterval) {
            clearInterval(refreshInterval);
        }
        if (!token) {
            return;
        }
        const body: {reason?: string} = {};
        if (reason) {
            body.reason = reason;
        }

        if (reason === 'Browser closed') {
            const isQueued = navigator.sendBeacon(
                releaseTokenBeaconUrl(token),
                new Blob([JSON.stringify(body)], {type: 'application/json'}),
            );
            if (!isQueued) {
                logger.debug('Failed to queue "release_token" beacon');
            }
        } else {
            await releaseTokenFn(withToken(fetcher, token), body);
        }
    };

    return {
        get token() {
            return token;
        },

        get expires() {
            return expires;
        },

        get statsUpdateInterval() {
            return statsUpdateInterval;
        },

        get fetcher() {
            return withToken(fetcher, token);
        },
        refreshToken: fireAndForgetRefresh,
        cleanup,
    };
};
